import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import axios from 'axios';
import { 
  Bars3Icon, 
  XMarkIcon, 
  ChevronDownIcon,
  HomeIcon,
  InformationCircleIcon,
  CubeIcon,
  MapPinIcon,
  PhoneIcon,
  CreditCardIcon,
  UsersIcon,
  CalendarIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

interface Destination {
  _id: string;
  name: string;
  image: string;
  isActive: boolean;
  showInMainNav?: boolean;
}

interface Package {
  _id: string;
  name: string;
  slug?: string;
  destination: string | { _id: string; name: string };
  priceUSD: number;
  priceAUD: number;
  lastMinuteDealPriceUSD?: number;
  lastMinuteDealPriceAUD?: number;
  lastMinuteDealExpiryDate?: string;
  hasActiveLastMinuteDeal?: boolean;
  maxGuests?: number;
  validityPeriod?: number;
  isLive: boolean;
  resorts?: Array<{
    _id: string;
    name: string;
    destination: string;
  } | string>;
}

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDestinationDropdown, setShowDestinationDropdown] = useState<string | null>(null);
  const [showOtherDestinationsDropdown, setShowOtherDestinationsDropdown] = useState(false);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const { currency } = useCurrency();
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/', icon: HomeIcon },
    { name: 'About', href: '/about', icon: InformationCircleIcon },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Fetch destinations and packages
  useEffect(() => {
    const fetchDestinationsAndPackages = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        
        // Fetch destinations and packages in parallel
        const [destResponse, pkgResponse] = await Promise.all([
          axios.get(`${apiUrl}/api/destinations?active=true`),
          axios.get(`${apiUrl}/api/packages?active=true`)
        ]);
        
        
        setDestinations(destResponse.data.destinations || []);
        setPackages(pkgResponse.data.packages || []);
      } catch (error) {
        console.error('Failed to fetch destinations/packages:', error);
      }
    };

    fetchDestinationsAndPackages();
  }, []);

  // Helper to get effective AUD price (deal price if available, otherwise regular price)
  const getEffectiveAUDPrice = (pkg: Package) => {
    const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
    const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
    const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
    
    if (hasDealPrice && isDealActive && dealPriceAUD) {
      return dealPriceAUD;
    }
    return pkg.priceAUD;
  };

  // Get packages for a specific destination (packages directly assigned to this destination)
  const getPackagesForDestination = (destinationId: string) => {
    if (!Array.isArray(packages)) {
      console.log('Packages is not an array:', packages);
      return [];
    }

    return packages
      .filter(pkg => {
        // Only show live packages
        if (!pkg.isLive) return false;
        const pkgDestId = typeof pkg.destination === 'object' ? pkg.destination._id : pkg.destination;
        return pkgDestId === destinationId;
      })
      .sort((a, b) => {
        // Sort by effective AUD price (deal price first, then regular price)
        const priceA = getEffectiveAUDPrice(a);
        const priceB = getEffectiveAUDPrice(b);
        return priceA - priceB;
      });
  };

  // Split destinations: those with showInMainNav=true go to main nav, rest to "Other Destinations"
  const mainDestinations = destinations.filter(d => d.showInMainNav === true);
  const otherDestinations = destinations.filter(d => d.showInMainNav !== true);

  // Get all packages from other destinations
  const getOtherDestinationsPackages = () => {
    if (!Array.isArray(packages) || otherDestinations.length === 0) return [];
    
    const otherDestIds = otherDestinations.map(d => d._id);
    return packages
      .filter(pkg => {
        // Only show live packages
        if (!pkg.isLive) return false;
        const pkgDestId = typeof pkg.destination === 'object' ? pkg.destination._id : pkg.destination;
        return otherDestIds.includes(pkgDestId);
      })
      .sort((a, b) => {
        const priceA = getEffectiveAUDPrice(a);
        const priceB = getEffectiveAUDPrice(b);
        return priceA - priceB;
      });
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (isOpen) setIsOpen(false);
      if (showDestinationDropdown) setShowDestinationDropdown(null);
      if (showOtherDestinationsDropdown) setShowOtherDestinationsDropdown(false);
    };

    if (isOpen || showDestinationDropdown) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, showDestinationDropdown]);

  return (
    <>
      {/* Body Padding for Fixed Header */}
      <div className="h-20"></div>
      
      {/* Professional Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Professional Logo */}
            <Link 
              to="/" 
              className="flex items-center space-x-3 group transition-all duration-300 hover:scale-105" 
              onClick={() => setIsOpen(false)}
            >
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-600 rounded-xl shadow-lg group-hover:shadow-orange-500/30 transition-all duration-300 flex items-center justify-center">
                  <img src="/logo.png" alt="Bali Getaway" className="h-8 w-8" />
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors">
                  Bali Getaway
                </span>
                <span className="text-xs font-medium text-orange-600 -mt-1 tracking-wide">
                  Tropical Paradise
                </span>
              </div>
            </Link>

            {/* Professional Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navigation.slice(0, 2).map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative px-4 py-2 font-semibold text-base transition-all duration-300 group ${
                    isActive(item.href)
                      ? 'text-orange-600'
                      : 'text-gray-700 hover:text-orange-600'
                  }`}
                >
                  {item.name}
                  <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-orange-500 to-yellow-500 transform transition-all duration-300 ${
                    isActive(item.href) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}></span>
                </Link>
              ))}

              {/* Professional Destination Dropdowns - Only first 2 destinations */}
              {Array.isArray(mainDestinations) && mainDestinations.map((destination) => {
                const destPackages = getPackagesForDestination(destination._id);
                const isDestActive = location.pathname.includes(`/destinations/${destination._id}`) ||
                  (location.pathname.includes(`/packages/`) && destPackages.some(pkg =>
                    location.pathname.includes(pkg._id.toString())));
                
                return (
                  <div 
                    key={destination._id} 
                    className="relative group"
                    onMouseEnter={() => destPackages.length > 0 && setShowDestinationDropdown(destination._id)}
                    onMouseLeave={() => {
                      // Longer delay to allow users to move to dropdown
                      setTimeout(() => {
                        setShowDestinationDropdown(null);
                      }, 300);
                    }}
                  >
                    <Link
                      to={`/destinations/${destination._id}`}
                      className={`relative px-4 py-2 font-semibold text-base transition-all duration-300 group flex items-center space-x-1 ${
                        isDestActive
                          ? 'text-orange-600'
                          : 'text-gray-700 hover:text-orange-600'
                      }`}
                    >
                      <span>{destination.name}</span>
                      {destPackages.length > 0 && (
                        <ChevronDownIcon className="h-4 w-4 transition-transform duration-300 group-hover:rotate-180" />
                      )}
                      <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-orange-500 to-yellow-500 transform transition-all duration-300 ${
                        isDestActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                      }`}></span>
                    </Link>

                    {/* Professional Dropdown */}
                    {showDestinationDropdown === destination._id && destPackages.length > 0 && (
                      <div 
                        className="absolute left-0 top-full w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 animate-in slide-in-from-top-2 duration-200"
                        onMouseEnter={() => {
                          // Keep dropdown open when hovering over it
                          setShowDestinationDropdown(destination._id);
                        }}
                        onMouseLeave={() => {
                          // Hide dropdown when leaving it
                          setShowDestinationDropdown(null);
                        }}
                      >
                        {/* Header */}
                        <div className="px-6 py-4 bg-gradient-to-r from-orange-500 to-yellow-600 text-white rounded-t-2xl">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                              <MapPinIcon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white text-base">{destination.name} Packages</h3>
                              <p className="text-white/90 text-sm">{destPackages.length} available packages</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Package List */}
                        <div className="p-4 space-y-2">
                          {destPackages.map((pkg) => (
                            <Link
                              key={pkg._id}
                              to={`/packages/${pkg._id}`}
                              onClick={() => {
                                console.log('Navigating to package:', pkg._id, pkg.name);
                                // Hide dropdown after click
                                setShowDestinationDropdown(null);
                              }}
                              className="group block p-4 rounded-xl hover:bg-orange-50 transition-all duration-300 border border-transparent hover:border-orange-200 hover:shadow-md"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900 group-hover:text-orange-700 transition-colors">
                                    {pkg.name}
                                  </h4>
                                  <div className="flex items-center space-x-4 mt-1">
                                    {pkg.maxGuests && (
                                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                                        <UsersIcon className="h-3 w-3" />
                                        <span>Up to {pkg.maxGuests}</span>
                                      </div>
                                    )}
                                    {pkg.validityPeriod && (
                                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                                        <CalendarIcon className="h-3 w-3" />
                                        <span>{pkg.validityPeriod} months</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  {(() => {
                                    const effectivePrice = getEffectiveAUDPrice(pkg);
                                    const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
                                    const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
                                    const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
                                    const isDeaal = hasDealPrice && isDealActive;
                                    
                                    return (
                                      <div>
                                        {isDeaal && (
                                          <div className="text-xs text-red-600 font-bold mb-1">⚡ DEAL</div>
                                        )}
                                        <div className="flex items-baseline gap-1">
                                          <div className={`font-bold ${isDeaal ? 'text-red-600' : 'text-orange-600'}`}>
                                            A${effectivePrice.toLocaleString()}
                                          </div>
                                          {isDeaal && (
                                            <div className="text-xs text-gray-500 line-through">
                                              A${pkg.priceAUD.toLocaleString()}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()
                                  }
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                        
                        {/* Footer */}
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 rounded-b-2xl">
                          <Link
                            to={`/destinations/${destination._id}`}
                            className="group flex items-center justify-center space-x-2 text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors"
                            onClick={() => {
                              // Hide dropdown after click
                              setShowDestinationDropdown(null);
                            }}
                          >
                            <span>View All {destination.name} Packages</span>
                            <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Other Destinations Dropdown */}
              {otherDestinations.length > 0 && (() => {
                const otherPackages = getOtherDestinationsPackages();
                return (
                  <div 
                    className="relative group"
                    onMouseEnter={() => setShowOtherDestinationsDropdown(true)}
                    onMouseLeave={() => {
                      setTimeout(() => {
                        setShowOtherDestinationsDropdown(false);
                      }, 300);
                    }}
                  >
                    <button
                      className={`relative px-4 py-2 font-semibold text-base transition-all duration-300 group flex items-center space-x-1 text-gray-700 hover:text-orange-600`}
                    >
                      <span>Other Destinations</span>
                      <ChevronDownIcon className="h-4 w-4 transition-transform duration-300 group-hover:rotate-180" />
                      <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-orange-500 to-yellow-500 transform transition-all duration-300 scale-x-0 group-hover:scale-x-100`}></span>
                    </button>

                    {/* Other Destinations Dropdown */}
                    {showOtherDestinationsDropdown && (
                      <div 
                        className="absolute left-0 top-full w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 animate-in slide-in-from-top-2 duration-200"
                        onMouseEnter={() => setShowOtherDestinationsDropdown(true)}
                        onMouseLeave={() => setShowOtherDestinationsDropdown(false)}
                      >
                        {/* Header */}
                        <div className="px-6 py-4 bg-gradient-to-r from-orange-500 to-yellow-600 text-white rounded-t-2xl">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                              <MapPinIcon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white text-base">Other Destinations</h3>
                              <p className="text-white/90 text-sm">{otherPackages.length} available packages</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Package List */}
                        <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
                          {otherPackages.map((pkg) => {
                            const destName = typeof pkg.destination === 'object' ? pkg.destination.name : 
                              otherDestinations.find(d => d._id === pkg.destination)?.name || '';
                            return (
                              <Link
                                key={pkg._id}
                                to={`/packages/${pkg._id}`}
                                onClick={() => setShowOtherDestinationsDropdown(false)}
                                className="group block p-4 rounded-xl hover:bg-orange-50 transition-all duration-300 border border-transparent hover:border-orange-200 hover:shadow-md"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 group-hover:text-orange-700 transition-colors">
                                      {pkg.name}
                                    </h4>
                                    <div className="flex items-center space-x-2 mt-1">
                                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{destName}</span>
                                      {pkg.maxGuests && (
                                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                                          <UsersIcon className="h-3 w-3" />
                                          <span>Up to {pkg.maxGuests}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    {(() => {
                                      const effectivePrice = getEffectiveAUDPrice(pkg);
                                      const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
                                      const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
                                      const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
                                      const isDeal = hasDealPrice && isDealActive;
                                      
                                      return (
                                        <div>
                                          {isDeal && (
                                            <div className="text-xs text-red-600 font-bold mb-1">⚡ DEAL</div>
                                          )}
                                          <div className="flex items-baseline gap-1">
                                            <div className={`font-bold ${isDeal ? 'text-red-600' : 'text-orange-600'}`}>
                                              A${effectivePrice.toLocaleString()}
                                            </div>
                                            {isDeal && (
                                              <div className="text-xs text-gray-500 line-through">
                                                A${pkg.priceAUD.toLocaleString()}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                        
                        {/* Footer - View All */}
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 rounded-b-2xl">
                          <Link
                            to="/destinations"
                            className="group flex items-center justify-center space-x-2 text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors"
                            onClick={() => setShowOtherDestinationsDropdown(false)}
                          >
                            <span>View All Destinations</span>
                            <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Contact Navigation */}
              {navigation.slice(2).map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative px-4 py-2 font-semibold text-base transition-all duration-300 group ${
                    isActive(item.href)
                      ? 'text-orange-600'
                      : 'text-gray-700 hover:text-orange-600'
                  }`}
                >
                  {item.name}
                  <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-orange-500 to-yellow-500 transform transition-all duration-300 ${
                    isActive(item.href) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}></span>
                </Link>
              ))}

              {/* Professional CTA Button */}
              <div className="flex items-center">
                <Link
                  to="/pay-now"
                  className="group flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-yellow-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-orange-600 hover:to-yellow-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <CreditCardIcon className="h-5 w-5" />
                  <span>Pay Now</span>
                  <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Modern Mobile Actions */}
            <div className="flex items-center lg:hidden space-x-3">
              {/* Mobile CTA */}
              <Link
                to="/pay-now"
                className="relative overflow-hidden bg-gradient-to-r from-orange-500 to-yellow-600 p-0.5 rounded-full shadow-xl shadow-orange-500/25"
              >
                <div className="flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-yellow-600 px-4 py-2 rounded-full text-white font-bold text-sm">
                  <CreditCardIcon className="h-4 w-4" />
                  <span>Book</span>
                </div>
              </Link>

              {/* Modern Mobile Menu Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(!isOpen);
                }}
                className="relative p-3 rounded-2xl bg-white/60 backdrop-blur-sm text-gray-700 hover:text-orange-600 hover:bg-white/80 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                aria-label="Toggle mobile menu"
              >
                <div className="w-6 h-6 relative">
                  <span className={`absolute h-0.5 w-6 bg-current rounded-full transform transition-all duration-300 ease-in-out ${
                    isOpen ? 'rotate-45 top-3' : 'top-1'
                  }`} />
                  <span className={`absolute h-0.5 w-6 bg-current rounded-full transform transition-all duration-200 ease-in-out top-3 ${
                    isOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'
                  }`} />
                  <span className={`absolute h-0.5 w-6 bg-current rounded-full transform transition-all duration-300 ease-in-out ${
                    isOpen ? '-rotate-45 top-3' : 'top-5'
                  }`} />
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Slide-out Menu */}
      <div className={`lg:hidden fixed inset-0 z-40 transition-all duration-300 ${isOpen ? 'visible' : 'invisible'}`}>
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${isOpen ? 'opacity-50' : 'opacity-0'}`}
          onClick={() => setIsOpen(false)}
        />
        
        {/* Menu Panel */}
        <div className={`absolute top-0 right-0 h-screen w-80 max-w-[85vw] bg-white shadow-2xl transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex flex-col h-full max-h-screen">
            {/* Menu Header - Fixed at top */}
            <div className="flex-shrink-0 bg-gradient-to-r from-yellow-500 to-orange-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img src="/logo.png" alt="Bali Getaway" className="h-8 w-8" />
                <span className="text-white font-bold text-lg">Menu</span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Navigation Links - Scrollable */}
            <div className="flex-1 min-h-0 px-6 py-4 overflow-y-auto">
              <div className="space-y-2 pb-4">
                {/* Standard navigation items (Home, About) */}
                {navigation.slice(0, 2).map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center space-x-4 px-4 py-4 rounded-xl transition-all duration-200 ${
                        isActive(item.href)
                          ? 'bg-orange-50 text-orange-600 border-l-4 border-orange-600'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                      }`}
                      onClick={() => setIsOpen(false)}
                    >
                      <IconComponent className="h-6 w-6" />
                      <span className="font-medium text-lg">{item.name}</span>
                    </Link>
                  );
                })}
                
                {/* First 2 destinations as main items in mobile menu */}
                {Array.isArray(mainDestinations) && mainDestinations.map((destination) => {
                  const destPackages = getPackagesForDestination(destination._id);
                  return (
                    <div key={destination._id} className="space-y-1">
                      <Link
                        to={`/destinations/${destination._id}`}
                        className={`flex items-center space-x-4 px-4 py-4 rounded-xl transition-all duration-200 ${location.pathname.includes(`/destinations/${destination._id}`) ? 'bg-orange-50 text-orange-600 border-l-4 border-orange-600' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'}`}
                        onClick={() => setIsOpen(false)}
                      >
                        <MapPinIcon className="h-6 w-6" />
                        <span className="font-medium text-lg">{destination.name}</span>
                      </Link>

                      {/* Packages for this destination */}
                      <div className="space-y-1 ml-10 border-l-2 border-orange-100 pl-4">
                        {destPackages.map((pkg) => (
                          <Link
                            key={pkg._id}
                            to={`/packages/${pkg._id}`}
                            className="flex items-center space-x-2 px-2 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                            onClick={() => setIsOpen(false)}
                          >
                            <CubeIcon className="h-4 w-4" />
                            <div>
                              <div className="font-medium text-sm">{pkg.name}</div>
                              <div className={`text-xs ${(() => {
                                const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
                                const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
                                const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
                                return hasDealPrice && isDealActive ? 'text-red-600' : 'text-orange-600';
                              })()}`}>
                                {(() => {
                                  const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
                                  const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
                                  const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
                                  return hasDealPrice && isDealActive ? '⚡ ' : 'From ';
                                })()}A${getEffectiveAUDPrice(pkg)}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Other Destinations section in mobile menu */}
                {otherDestinations.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center space-x-4 px-4 py-4 rounded-xl bg-gray-50 text-gray-700">
                      <MapPinIcon className="h-6 w-6" />
                      <span className="font-medium text-lg">Other Destinations</span>
                    </div>

                    {/* Packages from other destinations */}
                    <div className="space-y-1 ml-10 border-l-2 border-orange-100 pl-4">
                      {getOtherDestinationsPackages().map((pkg) => {
                        const destName = typeof pkg.destination === 'object' ? pkg.destination.name : 
                          otherDestinations.find(d => d._id === pkg.destination)?.name || '';
                        return (
                          <Link
                            key={pkg._id}
                            to={`/packages/${pkg._id}`}
                            className="flex items-center space-x-2 px-2 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                            onClick={() => setIsOpen(false)}
                          >
                            <CubeIcon className="h-4 w-4" />
                            <div>
                              <div className="font-medium text-sm">{pkg.name}</div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">{destName}</span>
                                <span className={`text-xs ${(() => {
                                  const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
                                  const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
                                  const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
                                  return hasDealPrice && isDealActive ? 'text-red-600' : 'text-orange-600';
                                })()}`}>
                                  {(() => {
                                    const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
                                    const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
                                    const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
                                    return hasDealPrice && isDealActive ? '⚡ ' : '';
                                  })()}A${getEffectiveAUDPrice(pkg)}
                                </span>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                      
                      {/* View All link */}
                      <Link
                        to="/destinations"
                        className="flex items-center space-x-2 px-2 py-2 rounded-lg text-orange-600 hover:bg-orange-50 transition-all duration-200 font-medium"
                        onClick={() => setIsOpen(false)}
                      >
                        <ArrowRightIcon className="h-4 w-4" />
                        <span className="text-sm">View All Destinations</span>
                      </Link>
                    </div>
                  </div>
                )}

                {/* Contact link */}
                {navigation.slice(2).map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center space-x-4 px-4 py-4 rounded-xl transition-all duration-200 ${
                        isActive(item.href)
                          ? 'bg-orange-50 text-orange-600 border-l-4 border-orange-600'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                      }`}
                      onClick={() => setIsOpen(false)}
                    >
                      <IconComponent className="h-6 w-6" />
                      <span className="font-medium text-lg">{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Menu Footer - Fixed at bottom */}
            <div className="flex-shrink-0 border-t border-gray-200 px-6 py-4">
              <Link
                to="/book-now"
                className="w-full flex items-center justify-center space-x-3 px-6 py-4 bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-bold rounded-xl hover:from-yellow-600 hover:to-orange-700 transition-all duration-300 shadow-lg"
                onClick={() => setIsOpen(false)}
              >
                <CreditCardIcon className="h-5 w-5" />
                <span>Pay Now</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
